import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { import_type_label } from '../../lib/metric_capture/import';
import { CLOUD_FUNCTION_URL } from '../../env';
import { ImportContext } from './ImportDataButton';
import MetricRecordFileImportContext from './contexts/MetricRecordFileImportContext';
import MetricRecordMeteredDataImportContext from './contexts/MetricRecordMeteredDataImportContext';
import SiteDataImportContext from './contexts/SiteDataImportContext';
import { User } from '@esg/esg-global-types';
import { UserContext } from '../../context/UserContext';
import {
  hardDeleteBulkMetricRecord,
  metric_record_label
} from '../../graphql/services/metric_record.service';
import { deleteImportedSitesByTrackingId, site_label } from '../../lib/metric_capture/site';

export interface ImportType {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  file_upload_dependancies?: Array<string>;
  displayContext?: (
    handleContextChange: (context: ImportContext) => void,
    handleContextValid: (valid: boolean) => void
  ) => JSX.Element;
  handleUndo?: (group_id: string, company_id: string, tracking_id: string) => Promise<void>;
  checkImportPermissions?: (user: User) => boolean;
}

interface ImportTypeSelectProps {
  handleChangeImportType: (import_type: ImportType | null) => void;
  options?: Array<ImportType>;
  selected_options?: ImportType | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  searchable?: boolean;
  color?: string;
  disable_underline?: boolean;
}

// Full list of potential imports to be used by other import components.
export const import_types: Array<ImportType> = [
  {
    id: 'file_import',
    name: `${metric_record_label.one} CSV`,
    description: `Import ${site_label.one.toLowerCase()} level ${metric_record_label.many} from CSV file`,
    endpoint: `${CLOUD_FUNCTION_URL}/importMetricRecordFromFile`,
    file_upload_dependancies: ['metric_records', 'mapping'],
    displayContext: (
      handleContextChange: (context: ImportContext) => void,
      handleContextValid: (valid: boolean) => void
    ) => (
      <MetricRecordFileImportContext
        handleContextChange={handleContextChange}
        handleContextValid={handleContextValid}
      />
    ),
    handleUndo: async (group_id: string, company_id: string, tracking_id: string) =>
      await hardDeleteBulkMetricRecord({ group_id, company_id }, tracking_id, {
        create_audit_log: true
      })
  },
  {
    id: 'metered_data',
    name: 'Metered Data',
    description: `Import ${site_label.one.toLowerCase()} level ${metric_record_label.many} from Utility Managment metered data`,
    endpoint: `${CLOUD_FUNCTION_URL}/importMetricRecordsMeteredData`,
    displayContext: (
      handleContextChange: (context: ImportContext) => void,
      handleContextValid: (valid: boolean) => void
    ) => (
      <MetricRecordMeteredDataImportContext
        handleContextChange={handleContextChange}
        handleContextValid={handleContextValid}
      />
    ),
    handleUndo: async (group_id: string, company_id: string, tracking_id: string) =>
      await hardDeleteBulkMetricRecord({ group_id, company_id }, tracking_id, {
        create_audit_log: true
      })
  },
  {
    id: 'site_import',
    name: `${site_label.one} Data CSV`,
    description: `Import ${site_label.many.toLowerCase()} for the current portal company`,
    endpoint: `${CLOUD_FUNCTION_URL}/importSitesFromCsv`,
    file_upload_dependancies: ['sites'],
    displayContext: (
      handleContextChange: (context: ImportContext) => void,
      handleContextValid: (valid: boolean) => void
    ) => (
      <SiteDataImportContext
        handleContextChange={handleContextChange}
        handleContextValid={handleContextValid}
      />
    ),
    handleUndo: async (group_id: string, company_id: string, tracking_id: string) =>
      await deleteImportedSitesByTrackingId(group_id, company_id, tracking_id),
    checkImportPermissions: (user: User) => user.super_admin
  }
];

/**
 * Import Type selection auto-complete component that handles a variety of built-in scenarios.
 * @param {ImportType | null} props.handleChangeImportType Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ImportType>} props.options Custom list of options provided by the parent component.
 * @param {ImportType | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @returns {JSX.Element}
 */
const ImportTypeSelect = (props: ImportTypeSelectProps) => {
  const { user } = React.useContext(UserContext);
  const [selectedOption, setSelectedOption] = React.useState<ImportType | null>(null);

  // Define selected options
  React.useEffect(() => {
    setSelectedOption(props.selected_options ? props.selected_options : null);
  }, [props.options, props.selected_options]);

  const allowed_import_types: Array<ImportType> = import_types.filter((import_type: ImportType) =>
    import_type.checkImportPermissions
      ? user
        ? import_type.checkImportPermissions(user)
        : false
      : true
  );

  return (
    <Autocomplete
      id="import-type-select"
      fullWidth
      disabled={props.disabled}
      disableClearable={props.clearable === false}
      options={props.options ?? allowed_import_types}
      value={selectedOption}
      getOptionLabel={(option: ImportType) => {
        return option.name;
      }}
      isOptionEqualToValue={(option: ImportType, value: ImportType) => option.id === value.id}
      noOptionsText={`No ${import_type_label.many.toLowerCase()}`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? import_type_label.one}
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(event: React.SyntheticEvent<Element, Event>, value: ImportType | null) => {
        setSelectedOption(value);
        props.handleChangeImportType(value);
      }}
    />
  );
};

export default ImportTypeSelect;
